import { Hand } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { PresenceMember } from "@canvaschool/contracts";

/** A raise older than this is stale even if the student's own tab never
 * sent the auto-lower (a dead tab, a closed laptop lid). This is the same
 * ten minutes as the student-side auto-lower timer in `CanvasEditor.tsx`;
 * it exists purely as a client-side safety net on top of that. */
const HAND_RAISE_TIMEOUT_MILLISECONDS = 10 * 60 * 1_000;

/** Nothing pushes a re-render once a raise crosses the ten-minute mark by
 * itself, so a tick on an interval is what actually retires a stale chip —
 * the same device `CoverOverlay` uses for its own countdown. */
const TICK_INTERVAL_MILLISECONDS = 5_000;

export type HandRaiseTrayProps = {
  roster: readonly PresenceMember[];
  now?: () => number;
};

const dismissKey = (studentNumber: number, raisedAt: string) =>
  `${studentNumber}:${raisedAt}`;

/**
 * Teacher-only. Every raised hand becomes a chip in the bottom-right
 * corner, oldest first, so the room reads as a queue. Dismissing a chip is
 * local-only — the student's `handRaised` stays true on the server and on
 * every other teacher's screen; only a fresh raise (a new `raisedAt`)
 * clears the dismissal here, since that is logically a new raise event.
 */
export const HandRaiseTray = ({
  roster,
  now = Date.now,
}: HandRaiseTrayProps) => {
  const [tick, setTick] = useState(0);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handle = window.setInterval(
      () => setTick((value) => value + 1),
      TICK_INTERVAL_MILLISECONDS,
    );
    return () => window.clearInterval(handle);
  }, []);

  const raised = useMemo(() => {
    void tick;
    const cutoff = now() - HAND_RAISE_TIMEOUT_MILLISECONDS;
    return roster
      .filter(
        (member) =>
          member.identity.role === "STUDENT" &&
          member.handRaised &&
          member.raisedAt !== null &&
          Date.parse(member.raisedAt) >= cutoff,
      )
      .sort(
        (left, right) =>
          Date.parse(left.raisedAt as string) -
          Date.parse(right.raisedAt as string),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, now, tick]);

  const visible = raised.filter(
    (member) =>
      !dismissed.has(
        dismissKey(
          member.identity.studentNumber ?? 0,
          member.raisedAt as string,
        ),
      ),
  );

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="canvaschool-hand-raise-tray">
      {visible.map((member) => {
        const key = dismissKey(
          member.identity.studentNumber ?? 0,
          member.raisedAt as string,
        );
        return (
          <button
            key={member.identity.connectionId}
            type="button"
            className="canvaschool-roster-panel-alert canvaschool-hand-raise-chip"
            onClick={() => setDismissed((current) => new Set(current).add(key))}
          >
            <Hand size={18} aria-hidden="true" />
            {member.identity.displayName}
          </button>
        );
      })}
    </div>
  );
};
